import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Dashboard from '@/components/Dashboard/Dashboard.vue';
import MyBoards from '@/components/Dashboard/MyBoards.vue';
import SharedBoards from '@/components/Dashboard/SharedBoards.vue';
import DashboardSidebar from '@/components/Dashboard/DashboardSidebar.vue';
import LabelManager from '@/components/Dashboard/Labels/LabelManager.vue';
import Canvas from '@/components/Canvas/Canvas.vue';
import CanvasSidebar from '@/components/Canvas/CanvasSidebar.vue';
import LogoutSidebar from '@/components/Canvas/LogoutSidebar.vue';
import Auth from '@/components/Auth/Auth.vue';
import AuthSidebar from '@/components/Auth/AuthSidebar.vue';
import AuthSidebarBottom from '@/components/Auth/AuthSidebarBottom.vue';
import WelcomeScreen from '@/components/Auth/WelcomeScreen.vue';
import PresenterLogin from '@/components/Auth/PresenterScreen/LoginForm.vue';
import PresenterRegister from '@/components/Auth/PresenterScreen/RegisterForm.vue';
import ForgotPassword from '@/components/Auth/PresenterScreen/ForgotPassword.vue';
import NewPassword from '@/components/Auth/PresenterScreen/NewPassword.vue';
import ViewerLogin from '@/components/Auth/ViewerScreen/LoginForm.vue';
import store from '../store/index';
import { SocketActionsKeys } from '@/store/types/socket';
import API from '@/plugins/axios';
import { BoardActionsKeys } from '@/store/types/board';
import { SessionActionsKeys, SessionMutationsKeys } from '@/store/types/session';
import { RootGettersReturns, ActionsKeys } from '@/store/types';

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/accept_board/:uuid',
    beforeEnter: (to, from, next) => {
      const board_uuid = to.params.uuid;
      if (board_uuid) {
        localStorage.setItem('board_uuid', board_uuid);
      }
      if (!store.getters.isLoggedIn) {
        next('/auth');
      } else {
        next('/dashboard');
      }
    },
  },
  {
    path: '/dashboard',
    components: {
      default: Dashboard,
      sidebar: DashboardSidebar,
      sidebarBottom: LogoutSidebar,
    },
    meta: {
      title: 'Dashboard',
      metaTags: [
        {
          name: 'description',
          content: 'Dashboard - A place to find all your boards',
        },
        {
          property: 'og:description',
          content: 'Dashboard - A place to find all your boards',
        },
      ],
    },
    beforeEnter: async (to, from, next) => {
      const search_term = to.query.search_term;
      if (search_term) {
        localStorage.setItem('search_term', search_term as string);
      }
      await store.dispatch(ActionsKeys.CHECK_LOGGED_IN);
      if (!store.getters.isLoggedIn) {
        next('/auth');
      } else {
        store.dispatch(SocketActionsKeys.CONNECT_SOCKET);
        API.defaults.headers.common['Authorization'] = `Bearer ${store.getters.jwt}`;
        store.dispatch(BoardActionsKeys.LIST_BOARDS);
        store.dispatch(BoardActionsKeys.LIST_FAVORITES);
        store.dispatch(BoardActionsKeys.LIST_LABELS);
        store.dispatch(SessionActionsKeys.LIST_SESSIONS);
        store.dispatch(BoardActionsKeys.LIST_SHARED_BOARDS);
        next();
      }
    },
    children: [
      {
        path: '/',
        component: MyBoards, // My Boards
      },
      {
        path: '/dashboard/shared',
        component: SharedBoards, // Shared boards
        meta: {
          title: 'Shared boards',
        },
      },
      {
        path: '/dashboard/labels',
        component: LabelManager, // Label Manager
        meta: {
          title: 'Label Manager',
        },
      },
    ],
  },
  {
    path: '/',
    name: 'Canvas',
    components: {
      default: Canvas,
      sidebar: CanvasSidebar,
      sidebarBottom: LogoutSidebar,
    },
    meta: {
      title: 'Smartboard',
      metaTags: [
        {
          name: 'description',
          content: 'Smartboard',
        },
        {
          property: 'og:description',
          content: 'Smartboard',
        },
      ],
    },
    beforeEnter: (to, from, next) => {
      let pin: string | null = to.query.pin as string;
      if (!store.getters.isLoggedIn) {
        if (pin) {
          localStorage.setItem('pin', pin);
        } else {
          pin = localStorage.getItem('pin');
        }
        let path = '/auth';
        if (pin) path = `/auth/viewer/login?pin=${pin}`;
        next(path);
      } else {
        store.dispatch(SocketActionsKeys.CONNECT_SOCKET);
        API.defaults.headers.common['Authorization'] = `Bearer ${store.getters.jwt}`;
        if ((store.getters as RootGettersReturns).isGuest) {
          if (!pin || Array.isArray(pin) || (store.getters as RootGettersReturns).sessionPin === atob(pin)) {
            store.commit(SessionMutationsKeys.SESSION_RESET_STATE);
            next();
          } else {
            const params = `?pin=${pin}`;
            next(`/auth/viewer/login${params}`);
          }
        } else if (!(store.getters as RootGettersReturns).isBoardOpen) {
          if (pin) {
            const params = `?pin=${pin}`;
            next(`/auth/viewer/login${params}`);
          } else {
            next({ path: '/dashboard' });
          }
        } else {
          if ((store.getters as RootGettersReturns).sessionJoined) {
            if (!pin || Array.isArray(pin) || (store.getters as RootGettersReturns).sessionPin === atob(pin)) {
              store.dispatch(SessionActionsKeys.JOIN_SESSION, (store.getters as RootGettersReturns).sessionPin);
              next();
            } else {
              const params = `?pin=${pin}`;
              next(`/auth/viewer/login${params}`);
            }
          } else {
            next();
          }
        }
      }
    },
  },
  {
    path: '/auth',
    name: 'Auth',
    components: {
      default: Auth,
      sidebar: AuthSidebar,
      sidebarBottom: AuthSidebarBottom,
    },
    meta: {
      title: 'Authorization',
      metaTags: [
        {
          name: 'description',
          content: 'Authorization page',
        },
        {
          property: 'og:description',
          content: 'Authorization page',
        },
      ],
    },
    beforeEnter: (to, from, next) => {
      const pin = to.query.pin;
      if (pin) {
        store.dispatch(ActionsKeys.LOGOUT, true);
        next();
      } else {
        if (store.getters.isLoggedIn) {
          next('/');
        } else {
          next();
        }
      }
    },
    children: [
      {
        path: '/',
        component: WelcomeScreen,
      },
      {
        path: '/auth/presenter/login',
        component: PresenterLogin,
        meta: {
          title: 'Login - Presenter',
        },
      },
      {
        path: '/auth/presenter/register',
        component: PresenterRegister,
        meta: {
          title: 'Register - Presenter',
        },
      },
      {
        path: '/auth/presenter/forgot_password',
        component: ForgotPassword,
        meta: {
          title: 'Forgot Password - Presenter',
        },
      },
      {
        path: '/auth/presenter/change_password/:uuid',
        component: NewPassword,
        meta: {
          title: 'Change Password - Presenter',
        },
      },
      {
        path: '/auth/viewer/login',
        component: ViewerLogin,
        meta: {
          title: 'Login - Viewer',
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// This callback runs before every route change, including on page load.
router.beforeEach((to, from, next) => {
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title);

  // Find the nearest route element with meta tags.
  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.metaTags);
  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) document.title = nearestWithTitle.meta.title;

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach(el => el.parentNode?.removeChild(el));

  // Skip rendering meta tags if there are none.
  if (!nearestWithMeta) return next();

  // Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags
    .map((tagDef: { [key: string]: string }) => {
      const tag = document.createElement('meta');

      Object.keys(tagDef).forEach(key => {
        tag.setAttribute(key, tagDef[key]);
      });

      // We use this to track which meta tags we create, so we don't interfere with other ones.
      tag.setAttribute('data-vue-router-controlled', '');

      return tag;
    })
    // Add the meta tags to the document head.
    .forEach((tag: HTMLElement) => document.head.appendChild(tag));

  next();
});
export default router;
